import { FC, ReactNode } from 'react';
import { SxProps, Theme, styled } from '@mui/material';
import { FocusTheme } from '@/components/Focus/configs/theme';
import ScheduleAvailChipButton from '@/components/Focus/AvailsSchedule/ScheduleAvailChipButton';
import { createAvailChipItems } from '@/components/Focus/utils/newScheduleAvails';
import { ScheduleAvailType } from '@/redux/slices/slice_types';

type DeltaType = 'added' | 'removed' | 'none';

export type AvailScheduleLaunchChipProps = {
  allLanguagesMap?: Record<string, { label: string }>;
  icon: ReactNode;
  items: string[];
  launchType: 'first-time' | 'incremental';
  previousItems?: string[];
  sx?: SxProps<Theme>;
  type: ScheduleAvailType;
};

const AvailScheduleLaunchChip: FC<AvailScheduleLaunchChipProps> = ({ allLanguagesMap, icon, items, launchType, previousItems = [], sx, type }) => {
  const chipItems = createAvailChipItems({
    values: items,
    type,
    allLanguagesMap
  });

  const isIncrementalLaunch = launchType === 'incremental';

  const addedCount = isIncrementalLaunch ? items.filter((value) => !previousItems.includes(value)).length : 0;
  const removedCount = isIncrementalLaunch ? previousItems.filter((value) => !items.includes(value)).length : 0;

  const hasAddedValues = addedCount > 0;
  const hasRemovedValues = removedCount > 0;

  const deltaLabel = hasAddedValues ? `+${addedCount}` : hasRemovedValues ? `-${removedCount}` : '–';
  const deltaType: DeltaType = hasAddedValues ? 'added' : hasRemovedValues ? 'removed' : 'none';

  return (
    <ScheduleAvailChipButton
      icon={icon}
      items={chipItems}
      caption={
        <StyledCaption>
          <StyledCount>{items.length}</StyledCount>
          <StyledDelta deltaType={deltaType}>{deltaLabel}</StyledDelta>
        </StyledCaption>
      }
      sx={sx}
    />
  );
};

export default AvailScheduleLaunchChip;

const StyledCaption = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const StyledCount = styled('span')`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: ${({ theme }) => theme.typography.pxToRem(16)};
`;

const StyledDelta = styled('span', {
  shouldForwardProp: (prop) => prop !== 'deltaType'
})<{ deltaType: DeltaType }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${({ theme }) => theme.spacing(3)};
  color: ${({ deltaType, theme }) => {
    if (deltaType === 'added') return theme.palette.primary.light;
    if (deltaType === 'removed') return theme.palette.error.main;

    return (theme as FocusTheme).palette.neutral[20];
  }};
  font-size: ${({ theme }) => theme.typography.pxToRem(16)};
`;
